import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import en from './en';
import hu from './hu';
import { isEmbedded } from '../config/embedded';

// Lightweight i18n for Outfino Insights — a locale context + a flat
// key→string dictionary per language (EN/HU). No external i18n dependency:
// simple key lookups with optional {var} fills, mirroring the admin panel.

const DICTS = { en, hu };

const STORAGE_KEY = 'portal_lang';

// Inside the Shopify admin the frame follows the admin's own language, English
// unless the admin is Hungarian. In the portal a language picked with the
// dashboard's switch wins (remembered, like the admin's); until then it follows
// the BROWSER: Hungarian browsers get HU, everyone else gets EN.
function readInitialLang() {
  if (isEmbedded()) {
    const adminLocale =
      window.shopify?.config?.locale || new URLSearchParams(window.location.search).get('locale');
    return String(adminLocale || '').toLowerCase().startsWith('hu') ? 'hu' : 'en';
  }
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'en' || saved === 'hu') return saved;
  } catch {
    // Storage blocked (Shopify admin iframe) — fall back to the browser.
  }
  const langs = [
    ...(navigator.languages || []),
    navigator.language,
    navigator.userLanguage,
  ].filter(Boolean);
  const isHu = langs.some((l) => String(l).toLowerCase().startsWith('hu'));
  return isHu ? 'hu' : 'en';
}

const LocaleContext = createContext({
  lang: 'en',
  setLang: () => {},
  t: (key) => key,
});

export function LocaleProvider({ children }) {
  const [lang, setLangState] = useState(readInitialLang);

  const setLang = useCallback((next) => {
    const value = next === 'hu' ? 'hu' : 'en';
    setLangState(value);
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      /* not remembered — see readInitialLang */
    }
  }, []);

  // t(key[, vars]) → translated string. Falls back to English, then to the
  // raw key, so a missing translation degrades visibly rather than crashing.
  const t = useCallback((key, vars) => {
    let s = DICTS[lang]?.[key] ?? DICTS.en[key] ?? key;
    if (vars) {
      for (const [k, v] of Object.entries(vars)) {
        s = s.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
      }
    }
    return s;
  }, [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  return useContext(LocaleContext);
}
